/**
 * Style system: picks a theme (piece-family weight biases), palette variant,
 * solid-vs-open policy, support flavor, and decor density. Emits weights and
 * predicates — never placements.
 */
import type { Rng } from "./rng.ts";
import type { PieceDef } from "./registry.ts";

/** Seed TEMPERAMENT (M24): a drawn identity that leans the ENDLESS
 * planner's hazard appetite and biome draw — worlds develop coherent
 * characters instead of uniform sampling. Finite mode never reads it
 * (genVersion stays put); the draws are APPENDED to the style stream. */
export interface Temperament {
  name: string;
  /** Endless clause-weight multipliers (path.ts combines with the
   * climate bias). */
  clauseBias: Record<string, number>;
  /** Island biome-weight multipliers (islands.ts). */
  biomeBias: Record<string, number>;
}

export const TEMPERAMENTS: readonly (Temperament & { weight: number })[] = [
  { name: "balanced", weight: 3, clauseBias: {}, biomeBias: {} },
  {
    name: "treacherous",
    weight: 1.5,
    clauseBias: { icy: 1.8, gravity: 1.5, spurDrop: 1.3 },
    biomeBias: { spooky: 2, barren: 1.5, winter: 1.4 },
  },
  {
    name: "soaring",
    weight: 1.5,
    clauseBias: { jump: 1.8, sprint: 1.4, helixDive: 1.3 },
    biomeBias: { tropical: 1.6, meadow: 1.3 },
  },
  {
    name: "tangled",
    weight: 1.2,
    clauseBias: { braid: 1.8, spurDrop: 1.4, reorient: 1.2 },
    biomeBias: { abstract: 1.6, grove: 1.4 },
  },
  {
    name: "rolling",
    weight: 1.2,
    clauseBias: { slope: 1.5, cornerDrop: 1.5, helixDive: 1.2 },
    biomeBias: { rocky: 1.5, autumn: 1.4 },
  },
  {
    name: "serene",
    weight: 1,
    clauseBias: { icy: 0.6, gravity: 0.6, jump: 0.8, braid: 0.8 },
    biomeBias: { meadow: 2, forest: 1.5, grove: 1.5 },
  },
];

export interface StyleField {
  theme: string;
  paletteIndex: 0 | 1 | 2;
  /** Multiplicative bias applied to a def's base weight at a ring index. */
  weightFor(def: PieceDef, ringIndex: number): number;
  /** Whether a track piece at this base height should use its solid variant. */
  solidAt(baseY: number): boolean;
  supportKind: "single" | "two" | "four";
  supportColor: boolean;
  /** 0..1 — decor placement probability scale. */
  decorDensity: number;
  /** 0..1 — the feature planner's appetite for wide-channel runs. */
  wideAffinity: number;
  /** Seed identity (M24) — endless-only hazard/biome leans. */
  temperament: Temperament;
}

interface ThemeSpec {
  name: string;
  weight: number;
  bias: Record<string, number>; // by def id or family
  solidGround: number; // chance ground-level pieces go solid
  decorDensity: number;
  wideAffinity: number;
}

const THEMES: ThemeSpec[] = [
  {
    name: "classic",
    weight: 4,
    bias: {},
    solidGround: 0.55,
    decorDensity: 0.5,
    wideAffinity: 0.3,
  },
  {
    name: "sweeping",
    weight: 3,
    bias: {
      "curve-large": 5,
      curve: 2,
      "s-curve": 2,
      corner: 0.25,
      tunnel: 0.5,
    },
    solidGround: 0.4,
    decorDensity: 0.45,
    wideAffinity: 0.35,
  },
  {
    name: "tight",
    weight: 3,
    bias: {
      corner: 3.5,
      bend: 3,
      curve: 0.6,
      "curve-large": 0.05,
      straight: 0.8,
    },
    solidGround: 0.5,
    decorDensity: 0.55,
    wideAffinity: 0.05,
  },
  {
    name: "canyon",
    weight: 2,
    bias: { tunnel: 5, slant: 1.6, ramp: 1.6, bump: 2 },
    solidGround: 0.95,
    decorDensity: 0.8,
    wideAffinity: 0.1,
  },
  // M2 themes: exploit the flow vocabulary.
  {
    name: "serpentine",
    weight: 3,
    bias: {
      bend: 6,
      "s-curve": 5,
      wave: 4,
      bump: 2.5,
      corner: 0.7,
      straight: 0.7,
      "curve-large": 0.4,
    },
    solidGround: 0.45,
    decorDensity: 0.5,
    wideAffinity: 0.15,
  },
  {
    name: "boulevard",
    weight: 2.5,
    bias: {
      "curve-large": 3,
      curve: 1.5,
      straight: 1.4,
      wave: 0.5,
      corner: 0.3,
      tunnel: 0.3,
    },
    solidGround: 0.6,
    decorDensity: 0.65,
    wideAffinity: 0.9,
  },
];

export function pickStyle(rng: Rng): StyleField {
  const spec = rng.weighted(THEMES.map((t) => [t, t.weight] as const));
  const paletteIndex = rng.int(0, 3) as 0 | 1 | 2;
  const supportKind = rng.weighted(
    [["single", 5], ["two", 3], ["four", 1.5]] as const,
  ) as "single" | "two" | "four";
  const supportColor = rng.chance(0.3);
  const solidGround = rng.chance(spec.solidGround);

  // Per-track zone offset makes the same theme vary between seeds: weights
  // gently oscillate around the ring in 2–4 zones.
  const zones = rng.int(2, 5);
  const zonePhase = rng.float() * Math.PI * 2;
  const zoneAmp = 0.25 + rng.float() * 0.35;

  // M24 temperament — APPENDED draw (finite streams byte-identical; the
  // endless planner and biome draw read it).
  const temperament = rng.weighted(
    TEMPERAMENTS.map((t) => [t, t.weight] as const),
  );

  return {
    theme: spec.name,
    paletteIndex,
    temperament,
    supportKind,
    supportColor,
    decorDensity: spec.decorDensity,
    wideAffinity: spec.wideAffinity,
    solidAt(baseY: number): boolean {
      // Elevated track reads better open (supports visible); ground track
      // follows the theme's solidness.
      return baseY <= 0 ? solidGround : false;
    },
    weightFor(def: PieceDef, ringIndex: number): number {
      const bias = spec.bias[def.id] ?? spec.bias[def.family] ?? 1;
      const zone = 1 + zoneAmp * Math.sin(zonePhase + ringIndex * zones * 0.05);
      return bias * zone;
    },
  };
}
